import { FC } from 'react';
import {
  Spacer,
  RadioGroup,
  Radio,
  Divider,
  Button,
  TextOverline,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  TranslationKeys,
} from '@pypestream/translations';

import { GenericPopover, GenericPopoverProps } from '../generic-popover';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../../xstate/app.xstate';
import { GetBotByIdData } from '../../../xstate/prostudio.api-service.types';

export type SolutionTypePopoverProps = GenericPopoverProps & {
  disabled?: boolean;
};
type BotType = GetBotByIdData['botType'];

const radioOptions: {
  label: string;
  value: BotType;
  i18nKey: TranslationKeys;
}[] = [
  {
    label: 'Main',
    value: 'main',
    i18nKey: 'pro-studio/navigation:settings.radio_group.radio1',
  },
  {
    label: 'Survey',
    value: 'survey',
    i18nKey: 'pro-studio/navigation:settings.radio_group.radio2',
  },
];

export const SolutionTypePopover: FC<SolutionTypePopoverProps> = ({
  trigger,
  popoverRef,
  onClose,
}) => {
  const { botType } = useProStudioCtxSelector((ctx) => ({
    botType: ctx.selectedBot?.botType || 'main',
  }));

  const onTypeChange = (type: BotType) => {
    if (!botType) {
      return;
    }

    sendProStudioEvent({
      type: 'setBotType',
      botType: type,
    });
  };

  return (
    <GenericPopover
      testId="solution-settings-popover"
      width="auto"
      trigger={trigger}
      popoverRef={popoverRef}
    >
      <TextOverline variant="tertiary">
        <TranslationComponent i18nKey="pro-studio/navigation:settings.title">
          Solution type
        </TranslationComponent>
      </TextOverline>
      <Spacer size="small" />
      <RadioGroup
        orientation="horizontal"
        size="small"
        value={botType}
        onChange={(e) => {
          onTypeChange?.(e.target.value as BotType);
        }}
      >
        {radioOptions.map((option) => (
          <Radio key={option.value} value={option.value}>
            <TranslationComponent i18nKey={option.i18nKey}>
              {option.label}
            </TranslationComponent>
          </Radio>
        ))}
      </RadioGroup>
      <Spacer />
      <Divider />
      <Spacer size="small" />
      <TextOverline variant="tertiary">Delete Solution</TextOverline>
      <Spacer size="small" />
      <Button
        test-id="delete-solution-button"
        size="large"
        variant="danger"
        width="full"
        onClick={onClose}
      >
        <TranslationComponent i18nKey="pro-studio/navigation:settings.button">
          Delete
        </TranslationComponent>
      </Button>
    </GenericPopover>
  );
};
